import { useEffect, useState } from "react";
import styled from "styled-components";
import { CommonLayout, CommmonBoxLayout, CommonBoxTitle, FlexColBetweenBox, CommonFooter } from "../style/common";
import Btn from "../components/common/Btn";
import { theme } from "../theme";
import { useDropzone } from "react-dropzone";
import { ReactComponent as TrashIcon } from "../svgs/trash_icon.svg"; // Assuming you have these or similar
import { fileApiService } from "../api/apiService";
import { postFiles } from "../api/files";
import { useAlertStore } from "../store/alertStore";
import { apiService } from "../api/apiService";
import { getContents, postContents, putContents, delContents } from "../api/contents";
import { contentsList } from "../api/contents";
import { useMemberStore } from "../store/memberStore";
import { gnbConst } from "../constants/gnbConst";
import { useNavigatePage } from "../hooks/useNavigatePage";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import config from "../config";

// Placeholder icons if not available, replace with actual imports
const Trash = () => <span>🗑️</span>;

interface TutorialItem {
  id: string;
  file: File | null;
  previewUrl: string;
  isNew: boolean;
  fileIdx?: string;
}

type VideoProvider = "youtube" | "vimeo";
const VIDEO_LINK_TITLE = "Tutorial Video Link";
const VIDEO_TITLE_FALLBACKS = [VIDEO_LINK_TITLE, "Tutorial Youtube Link"];

interface SortableItemProps {
  id: string;
  item: TutorialItem;
  onDelete: (id: string) => void;
}

function SortableItem({ id, item, onDelete }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ItemWrapper ref={setNodeRef} style={style} {...attributes}>
      <PreviewImg src={item.previewUrl} alt="preview" {...listeners} />
      <DeleteBtn onClick={(e) => {
        e.stopPropagation(); // Prevent drag start when clicking delete
        onDelete(id);
      }}>
        <Trash />
      </DeleteBtn>
    </ItemWrapper>
  );
}

export default function TutorialManager() {
  const [tutorialItems, setTutorialItems] = useState<TutorialItem[]>([]);
  const [youtubeLink, setYoutubeLink] = useState("");
  const [vimeoLink, setVimeoLink] = useState("");
  const [videoType, setVideoType] = useState<VideoProvider>("youtube");
  const { setAlert } = useAlertStore();
  const [isSpinner, setIsSpinner] = useState(false);
  const [guideListData, setGuideListData] = useState<ListGetApiResponse>();
  const { memberPermitLevel } = useMemberStore();
  const { onMovementPage } = useNavigatePage();

  useEffect(() => {
    if (gnbConst[0].level > memberPermitLevel) {
      alert("접근 권한이 없습니다.");
      onMovementPage(-1);
    }
  }, [memberPermitLevel]);

  const getGuideList = async () => {
    try {
      const params = {
        page_index: 1,
        page_size: 100,
      }
      const respones = await contentsList(params, "guide");
      setGuideListData(respones);
    } catch (error) { }
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const onDrop = (acceptedFiles: File[]) => {
    const newItems = acceptedFiles.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      previewUrl: URL.createObjectURL(file),
      isNew: true,
    }));
    setTutorialItems([...tutorialItems, ...newItems]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': []
    }
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setTutorialItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const deleteItem = (id: string) => {
    setTutorialItems(tutorialItems.filter((item) => item.id !== id));
  };

  const saveTutorialContent = async (currentItems: TutorialItem[], originalItems: TutorialItem[]) => {
    const selectedLink = videoType === "youtube" ? youtubeLink : vimeoLink;

    if (currentItems.length === 0 && !youtubeLink && !vimeoLink) {
      setAlert("저장할 튜토리얼 내용이 없습니다.", "gray");
      return;
    }

    // if (!selectedLink) {
    //   setAlert(`${videoType === "youtube" ? "유튜브" : "비메오"} 링크를 입력해주세요.`, "red");
    //   return;
    // }

    setIsSpinner(true);

    try {
      // 1. Identify items to delete (in original but not in current)
      const currentIds = new Set(currentItems.map(item => item.id));
      const itemsToDelete = originalItems.filter(item => !currentIds.has(item.id));

      const deletePromises = itemsToDelete.map(item =>
        delContents({ idx: item.id }, "guide")
      );

      // 2. Save/Update images
      const imagePromises = currentItems.map(async (item, index) => {
        if (!item.fileIdx) return null;

        const data = {
          title: `Tutorial Image ${index + 1}`,
          content: "",
          order_val: index + 1,
          file_idx: [item.fileIdx],
        };

        // If item is existing (not new) and has an ID that looks like a UUID (not random string), update it
        // Note: Our random IDs are 9 chars (Math.random), UUIDs are longer. 
        // Or we can check if it was in originalItems.
        const isExisting = originalItems.some(orig => orig.id === item.id);

        if (isExisting) {
          return putContents({ ...data, idx: item.id }, "guide");
        } else {
          return postContents(data, "guide");
        }
      });

      // 3. Save/Update/Delete Youtube Link
      // We need to find the original youtube link item to know if we should update or delete
      // But we don't store the youtube item in tutorialItems. 
      // We can find it in guideListData if we had access, but here we only have originalItems (which are images).
      // Wait, I should pass the original Youtube ID if it existed.
      // Let's assume we can find it from guideListData in the component scope.

      let videoPromise = null;
      const originalVideoItem = guideListData?.list.find(item => item.title && VIDEO_TITLE_FALLBACKS.includes(item.title));

      const data = {
        title: VIDEO_LINK_TITLE,
        url: selectedLink,
        order_val: currentItems.length + 1,
        extras: JSON.stringify({
          provider: videoType,
          youtube: youtubeLink,
          vimeo: vimeoLink,
        }),
      };

      if (originalVideoItem) {
        videoPromise = putContents({ ...data, idx: originalVideoItem.idx }, "guide");
      } else {
        videoPromise = postContents(data, "guide");
      }

      await Promise.all([...deletePromises, ...imagePromises, videoPromise].filter(Boolean));

      setAlert("튜토리얼이 저장되었습니다.", "green");
      getGuideList(); // Refresh list
    } catch (error) {
      console.error(error);
      setAlert("저장 중 오류가 발생했습니다. 다시 시도해주세요.", "red");
    } finally {
      setIsSpinner(false);
    }
  };

  const handleSave = async () => {
    let currentItems = [...tutorialItems];
    const newItems = currentItems.filter((item) => item.isNew && item.file);

    // Prepare original items list for sync comparison
    // We reconstruct it from guideListData to ensure we have the latest "server state" view
    // or we can rely on what we loaded initially. 
    // Better to rely on what we loaded initially + what we know.
    // Actually, `tutorialItems` contains both existing (loaded) and new items.
    // We need to know which ones were "original" to detect deletes.
    // We can filter `guideListData` again.

    const originalItems: TutorialItem[] = guideListData?.list
      .filter(item => item.files && item.files.length > 0 && item.title?.startsWith("Tutorial Image"))
      .map(item => ({
        id: item.idx,
        file: null,
        previewUrl: item.files?.[0]?.file_url || "",
        isNew: false,
        fileIdx: item.files?.[0]?.idx
      })) || [];

    if (newItems.length > 0) {
      try {
        const uploadResults = await Promise.all(
          newItems.map(async (item) => {
            if (!item.file) return null;

            const formData = new FormData();
            formData.append("file", item.file, "[PROXY]");
            formData.append("file_path", "tutorial");
            formData.append("file_type", "1");
            formData.append("permit_level", "0");

            const filesResponse = await fileApiService.post<any>("/files/post.php", formData);
            if (filesResponse.statusCode !== 200) throw new Error("파일 업로드 실패");

            const postFilesResponse = await postFiles(filesResponse.data);
            if (postFilesResponse.statusCode !== 200) throw new Error("메타데이터 저장 실패");

            const fileData = postFilesResponse.data;
            const baseUrl = config.front.url || "";
            const resolvedUrl = fileData.file_url?.startsWith("http")
              ? fileData.file_url
              : `${baseUrl}${fileData.file_url}`;

            return {
              id: item.id,
              previewUrl: resolvedUrl,
              fileIdx: fileData.idx,
            };
          })
        );

        currentItems = currentItems.map((item) => {
          const uploaded = uploadResults.find((u) => u && u.id === item.id);
          if (!uploaded) return item;
          return {
            ...item,
            file: null,
            isNew: false,
            previewUrl: uploaded.previewUrl,
            fileIdx: uploaded.fileIdx,
          };
        });

        setTutorialItems(currentItems);
      } catch (error) {
        console.error(error);
        setAlert("업로드 중 오류가 발생했습니다.", "red");
        return;
      }
    }

    await saveTutorialContent(currentItems, originalItems);
  };

  useEffect(() => {
    getGuideList();
  }, []);

  useEffect(() => {
    if (guideListData?.list) {
      const baseUrl = config.front.url || "";

      // 1. Parse Images
      const images = guideListData.list
        .filter(item => item.files && item.files.length > 0 && item.title?.startsWith("Tutorial Image"))
        .sort((a, b) => (a.order_val || 0) - (b.order_val || 0))
        .map(item => {
          const file = item.files![0];
          const resolvedUrl = file.file_url?.startsWith("http")
            ? file.file_url
            : `${baseUrl}${file.file_url}`;

          return {
            id: item.idx,
            file: null,
            previewUrl: resolvedUrl,
            isNew: false,
            fileIdx: file.idx
          } as TutorialItem;
        });

      setTutorialItems(images);

      // 2. Parse Youtube Link
      const videoItem = guideListData.list.find(item => item.title && VIDEO_TITLE_FALLBACKS.includes(item.title));
      if (videoItem) {
        try {
          const parsed = videoItem.extras ? JSON.parse(videoItem.extras) : null;
          if (parsed?.youtube) setYoutubeLink(parsed.youtube);
          else if (videoItem.title === "Tutorial Youtube Link" || videoItem.url) setYoutubeLink(videoItem.url || "");

          if (parsed?.vimeo) setVimeoLink(parsed.vimeo);

          if (parsed?.provider === "vimeo") {
            setVideoType("vimeo");
          } else {
            setVideoType("youtube");
          }
        } catch {
          setYoutubeLink(videoItem.url || "");
          setVimeoLink("");
          setVideoType("youtube");
        }
      } else {
        setYoutubeLink("");
        setVimeoLink("");
        setVideoType("youtube");
      }
    }
  }, [guideListData]);

  return (
    <CommonLayout>
      <CommmonBoxLayout>
        <FlexColBetweenBox>
          <ContentBox>
            <CommonBoxTitle>튜토리얼 관리</CommonBoxTitle>

            <Section>
              <SectionTitle>튜토리얼 이미지 관리</SectionTitle>
              <DropzoneBox {...getRootProps()}>
                <input {...getInputProps()} />
                <p>이미지를 드래그하거나 클릭하여 추가하세요.</p>
                <small>여러 장을 한 번에 업로드할 수 있습니다. (jpg, png 등)</small>
              </DropzoneBox>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <ScrollContainer>
                  <SortableContext
                    items={tutorialItems.map(item => item.id)}
                    strategy={horizontalListSortingStrategy}
                  >
                    <ItemList>
                      {tutorialItems.map((item) => (
                        <SortableItem key={item.id} id={item.id} item={item} onDelete={deleteItem} />
                      ))}
                    </ItemList>
                  </SortableContext>
                </ScrollContainer>
              </DndContext>

              {tutorialItems.length === 0 && <EmptyMsg>등록된 이미지가 없습니다.</EmptyMsg>}
            </Section>

            <Section>
              <SectionTitle>마지막 페이지 영상 링크</SectionTitle>
              <RadioGroup>
                <RadioLabel>
                  <input
                    type="radio"
                    name="videoType"
                    value="youtube"
                    checked={videoType === "youtube"}
                    onChange={() => setVideoType("youtube")}
                  />
                  유튜브
                </RadioLabel>
                <RadioLabel>
                  <input
                    type="radio"
                    name="videoType"
                    value="vimeo"
                    checked={videoType === "vimeo"}
                    onChange={() => setVideoType("vimeo")}
                  />
                  비메오
                </RadioLabel>
              </RadioGroup>
              <Input
                type="text"
                placeholder="유튜브 링크를 입력하세요 (예: https://youtu.be/...)"
                value={youtubeLink}
                onChange={(e) => setYoutubeLink(e.target.value)}
              />
              <Input
                type="text"
                placeholder="비메오 링크를 입력하세요 (예: https://vimeo.com/...)"
                value={vimeoLink}
                onChange={(e) => setVimeoLink(e.target.value)}
              />
            </Section>

          </ContentBox>

          <CommonFooter>
            <div></div>
            <Btn type="button" title="저장" color="primary" size="lg" onClick={handleSave} />
          </CommonFooter>
        </FlexColBetweenBox>
      </CommmonBoxLayout>
    </CommonLayout>
  );
}

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow-y: auto;
  padding-right: 1rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 600;
  color: #555;
`;

const DropzoneBox = styled.div`
  border: 2px dashed #ccc;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  background-color: #f9f9f9;
  transition: all 0.2s;

  &:hover {
    border-color: ${theme.color.primary};
    background-color: #f0f4ff;
  }

  p {
    color: #666;
    margin-bottom: 0.25rem;
  }

  small {
    color: #999;
  }
`;

const ScrollContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  padding-bottom: 1rem; // Space for scrollbar
  
  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #aaa;
  }
`;

const ItemList = styled.div`
  display: flex;
  gap: 1rem;
  min-width: min-content; // Ensure items don't shrink
`;

const ItemWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 313px; // Fixed width for consistent look
  height: 350px; // Aspect ratio roughly 16:9 or similar
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  cursor: grab;

  &:active {
    cursor: grabbing;
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
  }
`;

const PreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DeleteBtn = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.9);
  color: #ff4d4f;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  transition: all 0.2s;

  &:hover {
    background-color: #ff4d4f;
    color: #fff;
    transform: scale(1.1);
  }
`;

const EmptyMsg = styled.div`
  text-align: center;
  padding: 2rem;
  color: #999;
  background-color: #f9f9f9;
  border-radius: 0.375rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 0.375rem;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${theme.color.primary};
  }
`;
